<script setup lang="ts">
import { type PropType, ref, computed, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  patientsApi, DISCARD_REASONS, type DiscardReason, type PatientListItem,
} from '@/api/patients';
import { inventoryApi } from '@/api/inventory';
import { type SerializedUser } from '@/api/users';
import type { AxiosError } from 'axios';
import { usePermissions } from '@/composables/usePermissions';
import { camelize } from 'humps';
import { todayIso } from '@/utils/calendar-events';
import { formatShortDate } from '@/utils/patient-status';
import { requestErrorMessage } from '@/utils/api-errors';
import { authorIdentityErrorText } from '@/utils/author-identity';

const { hasPermission } = usePermissions();

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const props = defineProps({
  patient: {
    type: Object as PropType<PatientListItem>,
    required: true,
  },
  closeButtonText: {
    type: String,
    default: 'Cerrar',
  },
});

const currentUser = inject<Ref<SerializedUser>>('currentUser');

// Archiving never waits for the home to be emptied (returns usually lag the end
// of service); the warning points to where the remaining stock is handled.
const canSeeInventory = computed(() => (
  !!currentUser?.value?.companyInventoryEnabled && hasPermission.value('inventory', 'index')
));
const { data: homeStock } = useQuery({
  queryKey: ['inventoryHomeStock', props.patient.id],
  queryFn: async () => {
    const place = { patientId: props.patient.id };
    const [balances, units] = await Promise.all([
      inventoryApi.balances(place), inventoryApi.units({ ...place, status: 'in_service' }),
    ]);

    return { supplies: balances.filter((balance) => balance.tracking === 'quantity' && balance.quantity > 0).length,
      units: units.length };
  },
  enabled: canSeeInventory,
  initialData: { supplies: 0, units: 0 },
});
// Reason + end date are part of the Activos/Inactivos feature (care planning); the
// legacy dialog stays a plain confirmation for everyone else.
const withDetails = !!currentUser?.value?.companyCarePlanningEnabled;

const reasonOptions = DISCARD_REASONS.map((value) => ({
  value,
  title: t(`discardReasons.${camelize(value)}`),
}));

const lastShiftDate = computed(() => {
  const lastShiftAt = props.patient.status?.lastShiftAt;

  return lastShiftAt ? lastShiftAt.split('T')[0] : null;
});

const validationSchema = computed(() => yup.object(withDetails ? {
  discardReason: yup.string().required(t('patientDiscardForm.reasonRequired')),
  discardNote: yup.string().nullable(),
  endsOn: yup.string().nullable(),
} : {}));

const { handleSubmit, defineField, errors, values } = useForm<{
  discardReason: DiscardReason | null;
  discardNote: string | null;
  endsOn: string | null;
}>({
  validationSchema,
  initialValues: {
    discardReason: null,
    discardNote: null,
    // Local date, not toISOString(): after ~20:00 Chile time the UTC date is tomorrow.
    endsOn: lastShiftDate.value ?? todayIso(),
  },
});
const [discardReason, discardReasonAttrs] = defineField('discardReason');
const [discardNote, discardNoteAttrs] = defineField('discardNote');
const [endsOn, endsOnAttrs] = defineField('endsOn');

const submitButtonDisabled = ref(false);

const { mutate, isError, error } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => patientsApi.destroy(props.patient.id, withDetails ? {
      discardReason: values.discardReason,
      discardNote: values.discardNote || null,
      endsOn: values.endsOn || null,
    } : {}),
    onSuccess: () => {
      emit('refetch');
      emit('success');
      emit('close');
    },
    onError: () => { submitButtonDisabled.value = false; },
  },
);

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  mutate();
});

const discardErrorMessage = computed(() => {
  if (!isError.value || !error.value?.response?.status) return '';
  const patientHasShiftsError = 422;
  if (error.value.response.status === patientHasShiftsError) {
    return t('patientDiscardForm.error');
  }

  // A rejected reason/date comes back as a 400 with field errors; show them instead of
  // the generic "algo salió mal", which leaves the operator with nothing to correct.
  return requestErrorMessage(error.value, t('userSession.defaultError'));
});

// Closed the name and RUT dialog: archiving stops medication orders, a clinical write.
const errorMessage = computed(() => (
  (isError.value && authorIdentityErrorText(error.value)) || discardErrorMessage.value
));

const lastShiftHint = computed(() => (
  lastShiftDate.value ?
    `${t('patientStatus.columns.lastShift')}: ${formatShortDate(lastShiftDate.value)}` :
    t('patientDiscardForm.endsOnHint')
));
</script>
<template>
  <v-card
    v-if="hasPermission('patients', 'destroy')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('patientDiscardForm.title') }} {{ patient.fullName }}
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          <p class="mb-4">
            {{ withDetails ? t('patientDiscardForm.confirmationWithDetails') : t('patientDiscardForm.confirmation') }}
          </p>
          <template v-if="withDetails">
            <v-select
              v-model="discardReason"
              v-bind="discardReasonAttrs"
              :items="reasonOptions"
              :label="t('patientDiscardForm.reason')"
              :error-messages="errors.discardReason"
              variant="outlined"
              density="comfortable"
              data-testid="discard-reason"
            />
            <v-text-field
              v-model="endsOn"
              v-bind="endsOnAttrs"
              type="date"
              :label="t('patientDiscardForm.endsOn')"
              :hint="lastShiftHint"
              persistent-hint
              variant="outlined"
              density="comfortable"
              class="mt-2"
              data-testid="discard-ends-on"
            />
            <v-textarea
              v-model="discardNote"
              v-bind="discardNoteAttrs"
              :label="t('patientDiscardForm.note')"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              class="mt-2"
              data-testid="discard-note"
            />
          </template>
          <v-alert
            v-if="homeStock.supplies + homeStock.units > 0"
            type="warning"
            variant="tonal"
            class="mt-4"
            data-testid="discard-inventory-warning"
          >
            {{ t('patientDiscardForm.inventoryWarning', homeStock) }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn
            :text="props.closeButtonText"
            @click="$emit('close')"
          />
          <v-spacer />
          <v-btn
            color="red"
            type="submit"
            :disabled="submitButtonDisabled"
          >
            {{ t('patientDiscardForm.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ errorMessage }}
    </v-alert>
  </v-card>
</template>
